import { Box, BoxProps, Container, Theme, styled } from '@mui/material';
import { PoolInstance, isInstance } from '@synergy/config';
import { ReactElement } from 'react';
import { shouldForwardPropExclude } from '@synergy/utils';

import polygonLight from './header-polygon-light.svg';
import polygonLightLarge from './header-polygon-light-large.svg';
import polygonLightMobile from './header-polygon-light-mobile.svg';
import polygonMobile from './header-polygon-mobile.svg';

export type PoolPageHeaderVariant = 'white' | 'blue' | 'grey';

const colorsVariants = (
    theme: Theme
): Record<PoolPageHeaderVariant, { backgroundColor: string; gradient?: string; color: string }> => ({
    white: { backgroundColor: theme.palette.common.white, color: theme.palette.common.black },
    grey: {
        backgroundColor: '#2C3238', // grey950,
        color: theme.palette.common.white,
    },
    blue: {
        backgroundColor: 'rgba(16, 65, 112, 1)',
        gradient:
            'linear-gradient(90deg, rgba(16, 65, 112, 1) 25.53%, rgba(16, 65, 112, 0.614679) 51.04%, rgba(16, 65, 112, 0) 72.91%)',
        color: theme.palette.common.white,
    },
});

const getPolygons = (
    variant: PoolPageHeaderVariant,
    size?: 'medium' | 'large'
): { svgMobile: string; svgDesktop: string } => {
    if (isInstance(PoolInstance.CH)) {
        return { svgMobile: '', svgDesktop: '' };
    }
    const mobile = variant === 'white' ? polygonLightMobile : polygonMobile;
    return { svgMobile: mobile, svgDesktop: size === 'medium' ? polygonLight : polygonLightLarge };
};

const BannerImage = styled(Box, {
    shouldForwardProp: shouldForwardPropExclude('imageUrl', 'variant', 'size'),
})<{ variant: PoolPageHeaderVariant; imageUrl?: string; size?: 'medium' | 'large' }>(
    ({ theme, variant, imageUrl, size }) => {
        const { color, backgroundColor, gradient } = colorsVariants(theme)[variant];
        const { svgMobile, svgDesktop } = getPolygons(variant, size);

        return {
            color,
            backgroundColor,
            width: '100%',
            height: size === 'large' ? 144 : 120,
            backgroundPosition: 'right',
            backgroundRepeat: 'no-repeat',
            backgroundImage: [gradient ?? '', `url(${svgDesktop})`, imageUrl ? `url(${imageUrl})` : '']
                .filter(x => !!x)
                .join(','),

            [theme.breakpoints.down('sm')]: {
                height: size === 'large' ? 114 : 88,
                background: [gradient ?? '', `right/contain url(${svgMobile})`, imageUrl ? `url(${imageUrl})` : '']
                    .filter(x => !!x)
                    .join(','),
                backgroundColor,
            },
        };
    }
);

const StyledContainer = styled(Container)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    ...theme.typography.h4,
}));

export interface PoolPageHeaderProps extends BoxProps {
    variant?: PoolPageHeaderVariant;
    imageUrl?: string;
    size?: 'medium' | 'large';
}

export const PoolPageHeader = (props: PoolPageHeaderProps): ReactElement => {
    const { variant = 'white', imageUrl, size = 'medium', children, ...boxProps } = props;

    return (
        <BannerImage variant={variant} imageUrl={imageUrl} size={size} {...boxProps}>
            <StyledContainer maxWidth="xl">{children}</StyledContainer>
        </BannerImage>
    );
};
